import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";

import { useListInbound } from "../api/use-list-inbound";
import { InboundKpiStrip } from "../components/InboundKpiStrip";
import { InboundTable } from "../components/InboundTable";
import { downloadCsv, inboundToCsv } from "../lib/export-inbound-csv";
import {
  defaultInboundFilter,
  toggleSort,
  type InboundFilterState,
  type InboundSortKey,
} from "../lib/inbound-filter";

function InboundSkeleton(): JSX.Element {
  return (
    <div className="space-y-2 p-4" data-testid="inbound-loading">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-[var(--row-height-default)] animate-pulse rounded-[var(--r)] bg-[var(--surface-sunken)]"
        />
      ))}
    </div>
  );
}

interface InboundReportPageProps {
  highlight?: string;
}

export function InboundReportPage({ highlight }: InboundReportPageProps): JSX.Element {
  const { t } = useTranslation(["inbound_report", "nav", "common"]);
  const [filter, setFilter] = useState<InboundFilterState>(defaultInboundFilter);
  const [searchInput, setSearchInput] = useState(filter.search);
  const flashedRef = useRef<string | null>(null);

  const { data, isLoading, isError } = useListInbound(filter);

  const total = data?.total ?? 0;
  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const summary = data?.summary ?? null;

  // Deep-link flash: when rows are loaded and a highlight SKU is set, scroll
  // the first row matching that SKU into view and apply the flash animation.
  useEffect(() => {
    if (!highlight || rows.length === 0) return;
    if (flashedRef.current === highlight) return;
    flashedRef.current = highlight;

    const row = document.querySelector<HTMLElement>(`[data-sku="${highlight}"]`);
    if (!row) return;

    row.scrollIntoView({ behavior: "smooth", block: "center" });
    row.classList.add("row-flash");
    const timer = setTimeout(() => {
      row.classList.remove("row-flash");
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlight, rows]);

  function handleSort(key: InboundSortKey): void {
    setFilter((prev) => toggleSort(prev, key));
  }

  function submitSearch(): void {
    setFilter((prev) => ({ ...prev, search: searchInput.trim(), offset: 0 }));
  }

  function clearSearch(): void {
    setSearchInput("");
    setFilter((prev) => ({ ...prev, search: "", offset: 0 }));
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      e.preventDefault();
      submitSearch();
    }
  }

  function handleDateFrom(value: string): void {
    setFilter((prev): InboundFilterState => {
      const next: InboundFilterState = { ...prev, offset: 0 };
      if (value) {
        next.dateFrom = value;
      } else {
        delete next.dateFrom;
      }
      return next;
    });
  }

  function handleDateTo(value: string): void {
    setFilter((prev): InboundFilterState => {
      const next: InboundFilterState = { ...prev, offset: 0 };
      if (value) {
        next.dateTo = value;
      } else {
        delete next.dateTo;
      }
      return next;
    });
  }

  function handleExport(): void {
    downloadCsv("inbound-report.csv", inboundToCsv(rows));
  }

  return (
    <div data-testid="inbound-report-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.reports")}</span>
            <span className="text-[var(--text-subtle)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("nav:item.inbound_report")}</span>
          </>
        }
        title={t("inbound_report:page.title")}
        subtitle={t("inbound_report:page.subtitle", { count: total })}
        actions={
          <button
            type="button"
            onClick={handleExport}
            data-testid="inbound-export"
            className="inline-flex h-9 items-center gap-1.5 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-4)] text-[13.5px] font-semibold text-[var(--text-secondary)] shadow-[var(--sh-1)] transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-sunken)] focus-ring"
          >
            {t("inbound_report:toolbar.export")}
          </button>
        }
      />

      {summary && (
        <InboundKpiStrip
          docsInRange={summary.docs_in_range}
          totalReceived={summary.total_received}
          lastInboundAt={summary.last_inbound_at ?? null}
        />
      )}

      {/* Toolbar */}
      <div className="mb-[var(--sp-4)] flex flex-wrap items-center gap-[var(--sp-2)]">
        {/* Date range */}
        <label className="flex items-center gap-[var(--sp-2)] text-[13px] text-[var(--text-subtle)]">
          {t("inbound_report:toolbar.date_from")}
          <input
            type="date"
            value={filter.dateFrom ?? ""}
            onChange={(e) => handleDateFrom(e.target.value)}
            data-testid="inbound-date-from"
            className="h-9 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-2)] text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>
        <label className="flex items-center gap-[var(--sp-2)] text-[13px] text-[var(--text-subtle)]">
          {t("inbound_report:toolbar.date_to")}
          <input
            type="date"
            value={filter.dateTo ?? ""}
            onChange={(e) => handleDateTo(e.target.value)}
            data-testid="inbound-date-to"
            className="h-9 rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--sp-2)] text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
        </label>

        {/* Search */}
        <div className="relative min-w-[240px] max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
            aria-hidden
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t("inbound_report:toolbar.search_placeholder")}
            data-testid="inbound-search"
            className="h-9 w-full rounded-[var(--r)] border border-[var(--border-default)] bg-[var(--surface-raised)] pl-[38px] pr-9 text-sm text-[var(--text-primary)] outline-none transition focus:border-[var(--brand-500)] focus-ring"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label={t("common:action.clear")}
              className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-[var(--r-sm)] text-[var(--text-subtle)] transition hover:bg-[var(--surface-sunken)] focus-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* Content states */}
      {isError ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center text-sm text-[var(--text-secondary)] shadow-[var(--sh-1)]"
          data-testid="inbound-error"
        >
          {t("inbound_report:state.error")}
        </div>
      ) : isLoading ? (
        <div className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] shadow-[var(--sh-1)]">
          <InboundSkeleton />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-[var(--r-md)] border border-[var(--border-subtle)] bg-[var(--surface-raised)] py-16 text-center shadow-[var(--sh-1)]"
          data-testid="inbound-empty"
        >
          <p className="text-sm font-medium text-[var(--text-secondary)]">
            {t("inbound_report:state.empty")}
          </p>
        </div>
      ) : (
        <>
          <InboundTable rows={rows} filter={filter} onSort={handleSort} />
          <Pagination
            total={total}
            offset={filter.offset}
            limit={filter.limit}
            onChange={({ offset, limit }) => setFilter((prev) => ({ ...prev, offset, limit }))}
          />
        </>
      )}
    </div>
  );
}
